import React from 'react';

import useMenuExpandedState from '~/hooks/useMenuExpandedState';
import useNavigationLayout from '~/hooks/useNavigationLayout';
import useViewportType from '~/hooks/useViewportType';
import {
	isPhase3,
} from '~/utilities/viewportSize';

/**
 * Hook to automatically expand navigation from Phase 2 to Phase 3
 *
 * When called, this hook checks if we're in Phase 2 (mobile/responsive) with new navigation active,
 * and automatically expands the navigation to Phase 3 (desktop) to show labels.
 *
 * This is used when users interact with navigation items in Phase 2, providing a better UX
 * by temporarily expanding the navigation to show full labels.
 *
 * @returns Callback function that expands navigation if conditions are met
 *
 * @example
 * ```tsx
 * const handleAutoExpand = useAutoExpandNavigation();
 *
 * const handleClick = () => {
 *   handleAutoExpand(); // Expands navigation if in Phase 2
 *   // ... rest of click handler
 * };
 * ```
 */
function useAutoExpandNavigation(): () => void {
	const [
		currentLayout,
	] = useNavigationLayout();
	const viewportType = useViewportType();
	const [
		isMenuExpanded,
		setMenuExpanded,
		isMenuMinimized,
	] = useMenuExpandedState();

	const useNewNavigation = currentLayout === 'new';
	const effectivePhase3 = isPhase3(viewportType, isMenuExpanded, isMenuMinimized);
	const isCurrentlyPhase2 = !effectivePhase3;

	return React.useCallback(
		() => {
			// Only expand if we're in Phase 2 and new navigation is active
			if (isCurrentlyPhase2 && useNewNavigation) {
				setMenuExpanded(true);
			}
		},
		[isCurrentlyPhase2, useNewNavigation, setMenuExpanded],
	);
}

export default useAutoExpandNavigation;
